import { useEffect, useRef } from 'react';

import { Engine } from '../engine/index.js';

/**
 * @file Owns the engine's DOM container and its whole boot/teardown
 * lifecycle. Extracted from T0's `PlayRoute` (see `App.jsx` in this
 * project's git history / the T0 review) — that component proved this
 * survives React StrictMode's dev-mode double-invoke of effects. Every
 * consumer (currently only `PlayRoute`) gets that survival for free.
 *
 * StrictMode double-invoke runs mount -> cleanup -> mount synchronously,
 * before any promise settles. Three teardown moments this file is built to
 * survive, all exercised by that double-invoke plus normal navigation:
 *
 *  1. Unmount fires before `resolve()` (and therefore `new Engine()`) has
 *     even run — `cancelled` flips true while the local `engine` var is
 *     still null; every `await` point downstream re-checks `cancelled`
 *     before touching anything, so the (real) second mount is the only one
 *     that ever constructs an Engine.
 *  2. Unmount fires after `new Engine()` but before `await engine.init()`
 *     resolves — `engine.dispose()` is safe to call mid-init (Engine.js
 *     marks `_disposed` and defers the renderer's hard teardown until
 *     `init()`'s own promise settles and checks that flag).
 *  3. Unmount fires after `engine.start()` — full teardown path: loop
 *     stopped, systems/dayNight/post/level disposed, registry cleared,
 *     renderer disposed, and (critical) the canvas *this* Engine created
 *     inside the host div is removed. `hostRef.current` is the SAME div
 *     across a StrictMode double-mount (React doesn't recreate real DOM
 *     nodes for it), so the next Engine gets a clean container to mint its
 *     own canvas into. Never hand Engine a `<canvas>` directly — a WebGL2
 *     context that dies in `dispose()` poisons that canvas forever, and
 *     only a container lets each Engine own a fresh one.
 *
 * @param {Object} props
 * @param {() => Promise<{
 *   buildLevel: (ctx: import('../engine/types.js').LevelBuildCtx) => (import('../engine/types.js').Level|Promise<import('../engine/types.js').Level>),
 *   createPost?: ((engine: Engine) => import('../engine/types.js').PostStage)|null,
 *   engineOpts?: import('../engine/types.js').EngineOpts,
 *   params?: {seed?: string|number, id?: string, [k: string]: any},
 *   label?: string,
 * }>} props.resolve
 *   Produces the scene to boot. Called with **no arguments** — deliberately
 *   different from the originally-sketched `(engine) => ...` signature: the
 *   returned `engineOpts` (fov/pitch/yaw/distance/antialias/forceWebGL) has
 *   to be known *before* `new Engine()` runs, since that's the only place
 *   most of them can be applied, so an already-constructed engine can't be
 *   an input to this function. Must be referentially stable across renders
 *   that shouldn't reboot the engine (wrap it in `useCallback`) — GameCanvas
 *   only re-runs its boot effect when `resolve` itself changes identity.
 * @param {(engine: Engine|null) => void} [props.onEngine]
 *   Fired once synchronously right after `new Engine()` (before `init()`
 *   resolves, so `engine.bus` is already usable) and once more with `null`
 *   on every teardown. Store the value in a ref, not state — GameCanvas
 *   itself never puts the engine in React state and callers shouldn't either;
 *   pair it with a small state *counter* if you need a re-render trigger.
 * @param {(status: 'booting'|'running'|'error', detail?: any) => void} [props.onStatus]
 *   `'booting'` fires synchronously at the start of every boot attempt.
 *   `'running'` fires with `{label, backend}` once the level is built and
 *   the loop has started. `'error'` fires with the thrown error.
 * @param {string} [props.className] Extra classes on the container div.
 */
export default function GameCanvas({ resolve, onEngine, onStatus, className = '' }) {
  const hostRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    /** @type {Engine|null} */
    let engine = null;

    onStatus?.('booting');

    (async () => {
      const scene = await resolve();
      if (cancelled) return;

      // Pass the CONTAINER, not a canvas — see the file header.
      engine = new Engine(hostRef.current, scene.engineOpts ?? {});
      if (import.meta.env.DEV) {
        // Dev-only debugging hook, e.g. `__engine.dayNight.setHour(12)` from
        // the browser console.
        window.__engine = engine;
      }
      onEngine?.(engine);

      await engine.init();
      if (cancelled) return;

      if (scene.createPost) engine.post = scene.createPost(engine);
      await engine.setLevel(scene.buildLevel, scene.params ?? {});
      if (cancelled) return;

      engine.start();
      onStatus?.('running', {
        label: scene.label ?? '',
        backend: engine.caps.webgpu ? 'WebGPU' : 'WebGL2 fallback',
      });
    })().catch((err) => {
      console.error('[GameCanvas] engine boot failed', err);
      if (!cancelled) onStatus?.('error', err);
    });

    return () => {
      cancelled = true;
      engine?.dispose();
      if (import.meta.env.DEV && engine && window.__engine === engine) {
        window.__engine = null;
      }
      engine = null;
      onEngine?.(null);
    };
    // `onEngine`/`onStatus` are expected to be stable (useCallback) in every
    // caller; only `resolve`'s identity should reboot the engine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolve]);

  return <div ref={hostRef} className={`absolute inset-0 h-full w-full ${className}`} />;
}
